import type { CarFleetViewRecord, UserGroupRecordListPartner } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { FilterUnit, NoddiColumnDef } from 'noddi-ui-web';
import { ApiErrorMessageInternal, NoddiButton, NoddiTable, usePagination } from 'noddi-ui-web';
import { formatLicensePlate, formatPhoneNumber, getCarDisplayName } from 'noddi-util';
import { useMemo } from 'react';
import type { ImpersonationUser } from './ImpersonationConfirmation';
import { ImpersonationTableHeader } from './ImpersonationTableHeader';
import { getManagedCarsByOrganizationAndUser, getManagedCarsKey } from './managedCarsByUser';
import type { OrganizationImpersonationFilterInput } from './useImpersonationUserFilters';

const columns = (
  onSelectUser: (user: ImpersonationUser) => void,
  managedCarsByUser: ReadonlyMap<string, CarFleetViewRecord[]>
): NoddiColumnDef<UserGroupRecordListPartner>[] => [
  {
    id: 'organization',
    header: () => <ImpersonationTableHeader label='Organization' description='Organization customer name and ID.' />,
    accessorKey: 'name',
    size: 220,
    maxSize: 260,
    cell: ({ row }) => (
      <div className='flex flex-col'>
        <span className='font-medium'>{row.original.name}</span>
        <span className='text-xs text-muted-foreground'>ID {row.original.id}</span>
      </div>
    )
  },
  {
    id: 'members',
    size: 760,
    minSize: 640,
    header: () => (
      <ImpersonationTableHeader
        label='Members'
        description='Users available for impersonation, their contact details, and cars for which they are the assigned manager or driver.'
      />
    ),
    cell: ({ row }) => (
      <div className='flex flex-col gap-2 py-1'>
        {row.original.users.map((user) => {
          const managedCars = managedCarsByUser.get(getManagedCarsKey(row.original.id, user.id)) ?? [];

          return (
            <div key={user.id} className='flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs'>
              <NoddiButton
                className='h-auto min-w-40 justify-start p-0 text-sm'
                variant='lightLink'
                onClick={() => onSelectUser({ ...user, name: `${user.firstName} ${user.lastName}`.trim() })}
              >
                {user.firstName} {user.lastName}
              </NoddiButton>
              <span className='min-w-32 text-muted-foreground'>
                {user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : 'No phone number'}
              </span>
              <span className='min-w-52 text-muted-foreground'>{user.email || 'No email address'}</span>
              {managedCars.length > 0 && (
                <span className='flex min-w-0 flex-1 flex-wrap gap-x-3 text-muted-foreground'>
                  {managedCars.map((car) => (
                    <span key={car.id}>
                      {getCarDisplayName({ car, showLicensePlate: false })} ·{' '}
                      {formatLicensePlate(car.licensePlate.number)}
                    </span>
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </div>
    )
  }
];

export const ImpersonationOrganizationsTable = ({
  onSelectUser,
  filters
}: {
  onSelectUser: (user: ImpersonationUser) => void;
  /** Organization customers only: the dialog locks the type. */
  filters: FilterUnit<OrganizationImpersonationFilterInput>;
}) => {
  const { pagination, setPagination } = usePagination();
  const { data, isPending, isFetching, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupsIgnoringImpersonation,
    input: {
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search: pagination.search || undefined,
      ...filters.apiInput
    },
    queryConfig: { placeholderData: keepPreviousData }
  });

  const organizationIds = useMemo(() => data?.results.map((organization) => organization.id) ?? [], [data?.results]);
  const {
    data: cars,
    isLoading: isCarsPending,
    isFetching: isCarsFetching,
    error: carsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedFleetViewCarsIgnoringImpersonation,
    input: { userGroupIds: organizationIds },
    queryConfig: { enabled: organizationIds.length > 0 }
  });
  const managedCarsByUser = useMemo(
    () => getManagedCarsByOrganizationAndUser(cars?.results ?? [], organizationIds),
    [cars?.results, organizationIds]
  );

  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }

  if (carsError) {
    return <ApiErrorMessageInternal error={carsError} />;
  }

  return (
    <NoddiTable
      fillHeight
      data={data?.results ?? []}
      columns={columns(onSelectUser, managedCarsByUser)}
      pagination={pagination}
      setPagination={setPagination}
      totalCount={data?.count}
      isPending={isPending || isCarsPending}
      isFetching={isFetching || isCarsFetching}
      getRowId={(row) => row.id}
      searchPlaceholder='Search organizations'
      filters={filters}
      disableRowMenu
    />
  );
};
